import type { Dispatch, SetStateAction } from 'react'
import type {
  GridColDef,
  GridPaginationModel,
} from '@mui/x-data-grid-premium'

import LeadTableDateFilter from './LeadTableDateFilter'
import type { LeadTableDateFilterValue } from './LeadTableDateFilter'
import LeadTableLookupFilter, {
  type LeadTableLookupFilterGroup,
  type LeadTableLookupFilterValue,
} from './LeadTableLookupFilter'
import type { ExternalDateFilterField, ExternalFilters, ExternalLookupFilterField } from '../leadTableQuery'
import type { LeadRow, LookupMap, StringOptionMap } from '../table/leadTableTypes'
import { renderBooleanHeaderCheckboxFilter, renderHeaderWithFilter } from '../table/leadTableCellHelpers'


type BuildLeadTableColumnsWithFiltersParams = {
  columns: GridColDef<LeadRow>[]
  externalFilters: ExternalFilters
  timezone: string
  clientIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  degreeProgramsByClient: LeadTableLookupFilterGroup[]
  statuses: StringOptionMap
  dispositions: StringOptionMap
  onExternalFiltersChange: Dispatch<SetStateAction<ExternalFilters>>
  onPaginationModelChange: Dispatch<SetStateAction<GridPaginationModel>>
}

function updateExternalFilters(params: {
  onExternalFiltersChange: Dispatch<SetStateAction<ExternalFilters>>
  onPaginationModelChange: Dispatch<SetStateAction<GridPaginationModel>>
  updater: (prev: ExternalFilters) => ExternalFilters
}) {
  const {
    onExternalFiltersChange,
    onPaginationModelChange,
    updater,
  } = params

  onExternalFiltersChange(updater)
  onPaginationModelChange((prev) => ({ ...prev, page: 0 }))
}

function buildDateFilterHeader(params: {
  field: ExternalDateFilterField
  title: string
  externalFilters: ExternalFilters
  timezone: string
  onExternalFiltersChange: Dispatch<SetStateAction<ExternalFilters>>
  onPaginationModelChange: Dispatch<SetStateAction<GridPaginationModel>>
}) {
  const {
    field,
    title,
    externalFilters,
    timezone,
    onExternalFiltersChange,
    onPaginationModelChange,
  } = params

  return renderHeaderWithFilter({
    filterNode: (
        <LeadTableDateFilter
          title={title}
          timezone={timezone}
          value={externalFilters[field] as LeadTableDateFilterValue | undefined}
          onChange={(value) => {
          updateExternalFilters({
            onExternalFiltersChange,
            onPaginationModelChange,
            updater: (prev) => {
              const next = { ...prev }

              if (!value) {
                delete next[field]
              } else {
                next[field] = value
              }

              return next
            },
          })
        }}
      />
    ),
  })
}

function buildLookupFilterHeader(params: {
  field: ExternalLookupFilterField
  title?: string
  lookup: Record<string, string>
  value?: LeadTableLookupFilterValue
  groups?: LeadTableLookupFilterGroup[]
  filterExclusive?: boolean
  externalFilters: ExternalFilters
  onExternalFiltersChange: Dispatch<SetStateAction<ExternalFilters>>
  onPaginationModelChange: Dispatch<SetStateAction<GridPaginationModel>>
}) {
  const {
    field,
    title = '',
    lookup,
    value,
    groups,
    filterExclusive,
    onExternalFiltersChange,
    onPaginationModelChange,
  } = params

  return renderHeaderWithFilter({
    filterNode: (
      <LeadTableLookupFilter
        title={title}
        lookup={lookup}
        value={value}
        groups={groups}
        filterExclusive={filterExclusive}
        onChange={(nextValue) => {
          updateExternalFilters({
            onExternalFiltersChange,
            onPaginationModelChange,
            updater: (prev) => {
              const next = { ...prev }

              if (!nextValue) {
                delete next[field]
              } else {
                next[field] = nextValue
              }

              return next
            },
          })
        }}
      />
    ),
  })
}

export function buildLeadTableColumnsWithFilters({
  columns,
  externalFilters,
  timezone,
  clientIdToName,
  vendorIdToName,
  degreeProgramIdToName,
  degreeProgramsByClient,
  statuses,
  dispositions,
  onExternalFiltersChange,
  onPaginationModelChange,
}: BuildLeadTableColumnsWithFiltersParams): GridColDef<LeadRow>[] {
  return columns.map((column) => {
    switch (column.field) {
      case 'createdAt':
        return {
          ...column,
          renderHeader: () =>
            buildDateFilterHeader({
              field: 'createdAt',
              title: 'Date',
              externalFilters,
              timezone,
              onExternalFiltersChange,
              onPaginationModelChange,
            }),
        }

      case 'statusChangedAt':
        return {
          ...column,
          renderHeader: () =>
            buildDateFilterHeader({
              field: 'statusChangedAt',
              title: 'Status Changed',
              externalFilters,
              timezone,
              onExternalFiltersChange,
              onPaginationModelChange,
            }),
        }

      case 'clientId':
        return {
          ...column,
          renderHeader: () =>
            buildLookupFilterHeader({
              field: 'clientId',
              title: 'Client',
              lookup: clientIdToName,
              value: externalFilters.clientId,
              externalFilters,
              onExternalFiltersChange,
              onPaginationModelChange,
            }),
        }

      case 'degreeProgramId':
        return {
          ...column,
          renderHeader: () =>
            buildLookupFilterHeader({
              field: 'degreeProgramId',
              title: 'Degree Program',
              lookup: degreeProgramIdToName,
              value: externalFilters.degreeProgramId,
              groups: degreeProgramsByClient,
              externalFilters,
              onExternalFiltersChange,
              onPaginationModelChange,
            }),
        }

      case 'vendorId':
        return {
          ...column,
          renderHeader: () =>
            buildLookupFilterHeader({
              field: 'vendorId',
              title: 'Vendor',
              lookup: vendorIdToName,
              value: externalFilters.vendorId,
              externalFilters,
              onExternalFiltersChange,
              onPaginationModelChange,
            }),
        }

      case 'status':
        return {
          ...column,
          renderHeader: () =>
            buildLookupFilterHeader({
              field: 'status',
              title: 'Status',
              lookup: statuses,
              value: externalFilters.status,
              externalFilters,
              onExternalFiltersChange,
              onPaginationModelChange,
            }),
        }

      case 'disposition':
        return {
          ...column,
          renderHeader: () =>
            buildLookupFilterHeader({
              field: 'disposition',
              title: 'Disposition',
              lookup: dispositions,
              value: externalFilters.disposition,
              externalFilters,
              onExternalFiltersChange,
              onPaginationModelChange,
            }),
        }

      case 'billable':
        return {
          ...column,
          renderHeader: () =>
            buildLookupFilterHeader({
              field: 'billable',
              title: 'Billable',
              lookup: { true: 'Yes', false: 'No' },
              value: externalFilters.billable,
              filterExclusive: true,
              externalFilters,
              onExternalFiltersChange,
              onPaginationModelChange,
            }),
        }

      case 'secondaryTcpa':
        return {
          ...column,
          renderHeader: () =>
            buildLookupFilterHeader({
              field: 'secondaryTcpa',
              title: 'Secondary TCPA',
              lookup: { true: 'Yes', false: 'No' },
              value: externalFilters.secondaryTcpa,
              filterExclusive: true,
              externalFilters,
              onExternalFiltersChange,
              onPaginationModelChange,
            }),
        }

      case 'test':
        return {
          ...column,
          renderHeader: () =>
            renderHeaderWithFilter({
              title: 'Test',
              filterNode: renderBooleanHeaderCheckboxFilter({
                checked: Boolean(externalFilters.test),
                onChange: (checked) => {
                  updateExternalFilters({
                    onExternalFiltersChange,
                    onPaginationModelChange,
                    updater: (prev) => {
                      const next = { ...prev }

                      if (checked) {
                        next.test = true
                      } else {
                        delete next.test
                      }

                      return next
                    },
                  })
                },
              }),
            }),
        }

      default:
        return column
    }
  })
}
